import React, { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import { mdiArrowRight, mdiChevronRight } from "@mdi/js";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import ScrollCarousel from "./ScrollCarousel";
import useBreakpoint from "../../../../utils/useBreakpoint";
import type { TravelMode } from "../Homepage";
import LocationsApi from "../../../../api-services/locations-api.service";
import type { Location } from "../../../../interfaces/country.interface";

interface Props {
  mode?: TravelMode;
  locationGroupCodes?: string[];
}

const locationsApi = new LocationsApi();
const FALLBACK_IMAGE = "/iceholidays-assets/images/destination-background.png";

function listingLink(location: Location) {
  return `/web/listing?keyword=${encodeURIComponent(location.name)}&location_id=${location.id}`;
}

function normalizeCoverUrl(url?: string) {
  if (!url) return FALLBACK_IMAGE;

  try {
    const parsedUrl = new URL(url, window.location.origin);

    if (parsedUrl.pathname.startsWith("/rails/active_storage/")) {
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    }
  } catch (_error) {
    return url;
  }

  return url;
}

// Luggage-tag style capsule destination cards on a sky-blue rounded panel, with a
// trailing "see more" tag. In cruise mode the title switches to "Popular Right Now"
// (two-tone) per Figma. On desktop the cards sit in a grid; on mobile they move
// into the shared horizontal scroll-snap carousel (a single scrollable row).
export default function ExploreDestinations({
  mode = "tour",
  locationGroupCodes = ["GD_Standard", "GD_In_Depth"],
}: Props) {
  const isCruise = mode === "cruise";
  const isMobile = useBreakpoint();
  const [destinations, setDestinations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const groupKey = useMemo(() => locationGroupCodes.join(","), [locationGroupCodes]);

  useEffect(() => {
    let active = true;

    setLoading(true);
    locationsApi.getLocations({ group: locationGroupCodes, highlighted: true })
      .then(({ locations }) => {
        if (active) setDestinations(locations as Location[]);
      })
      .catch(() => {
        if (active) setDestinations([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [groupKey]);

  if (!loading && destinations.length === 0) return null;

  const cards = [
    ...destinations.map((destination) => (
      <Link key={destination.id} to={listingLink(destination)} className="v2-tag-card">
        <div className="v2-tag-card__frame">
          <div className="v2-tag-card__photo">
            <span className="v2-tag-card__img" style={{ backgroundImage: `url(${normalizeCoverUrl(destination.cover)})` }} aria-hidden="true" />
            <span className="v2-tag-card__strap" aria-hidden="true" />
            <span className="v2-tag-card__label">{destination.name}</span>
          </div>
        </div>
      </Link>
    )),
    <Link key="more" to="/web" className="v2-tag-card v2-tag-card--more">
      <div className="v2-tag-card__frame">
        <div className="v2-tag-card__photo v2-tag-card__photo--more">
          <span className="v2-tag-card__img" style={{ backgroundImage: `url(/iceholidays-assets/images/destinations/more.png)` }} aria-hidden="true" />
          <span className="v2-tag-card__strap" aria-hidden="true" />
          <span className="v2-tag-card__more-btn" aria-hidden="true">
            <Icon path={mdiArrowRight} size="22px" />
          </span>
          <span className="v2-tag-card__more-label">More</span>
        </div>
      </div>
    </Link>,
  ];

  const skeletonCards = Array.from({ length: isMobile ? 3 : 10 }, (_, index) => (
    <div key={index} className="v2-tag-card">
      <Skeleton.Image active />
    </div>
  ));

  return (
    <section id="explore-destinations-v2">
      <div className="v2-explore__panel">
        <div className="v2-explore__header">
          <div>
            <h2 className="v2-section__title">
              {isCruise ? (
                <>
                  <span className="v2-explore__title-accent">Popular</span> Right Now
                </>
              ) : (
                "Explore More Destinations"
              )}
            </h2>
            <p className="v2-section__subtitle">What other travellers are booking</p>
          </div>
          {/* Mobile-only: the trailing "More" card sits off-screen in the carousel,
              so surface a header "View all" to match the other sections. */}
          <Link to="/web" className="v2-section__viewall">
            View all <Icon path={mdiChevronRight} size="20px" />
          </Link>
        </div>

        {isMobile ? (
          <ScrollCarousel trackClassName="v2-explore__track" step={166}>
            {loading ? skeletonCards : cards}
          </ScrollCarousel>
        ) : (
          <div className="v2-explore__grid">{loading ? skeletonCards : cards}</div>
        )}
      </div>
    </section>
  );
}
